import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Target, Sparkles, ChevronDown, ChevronUp, Check, ExternalLink,
  Share2, Globe, Users, Star, Link2, Filter, Search, Clock,
  CheckCircle, ArrowRight, Zap, RefreshCw, AlertTriangle, Camera, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "../contexts/AppContext";
import { DEMO } from "../constants/mockData";
import { QUEST_TYPE_CONFIG, QUEST_TYPES } from "../constants/quests";
import { questsAPI } from "../services/api";

// Generate tracking link for visit-type quests
const generateTrackingLink = (questId, taskId, targetUrl) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Add UTM parameters for tracking
  const trackingParams = `?ref=burnz&quest=${questId}&task=${taskId}`;
  return targetUrl.includes('?') 
    ? `${targetUrl}&ref=burnz&quest=${questId}&task=${taskId}`
    : `${targetUrl}${trackingParams}`;
};

// Quest Type Icons
const QuestTypeIcon = ({ type, className = "w-5 h-5" }) => {
  const icons = {
    share: Share2,
    visit: Globe,
    follow: Users,
    review: Star,
    referral: Link2,
  };
  const Icon = icons[type] || Target;
  return <Icon className={className} />;
};

// Quest Type Badge
const QuestTypeBadge = ({ type }) => {
  const config = QUEST_TYPE_CONFIG[type];
  if (!config) return null;
  
  return (
    <Badge className={`bg-${config.color}-500/20 text-${config.color}-400 border-0 text-xs`}>
      {config.icon} {config.label}
    </Badge>
  );
};

// Quest Card Component (Redesigned)
const QuestCard = ({ quest }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [completedTaskIds, setCompletedTaskIds] = useState(() => {
    const stored = localStorage.getItem(`quest_${quest.id}_completed`) || '[]';
    return JSON.parse(stored);
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [proofScreenshot, setProofScreenshot] = useState(null);
  const [proofUrl, setProofUrl] = useState('');
  const [uploadingProof, setUploadingProof] = useState(false);

  const isTaskCompleted = (taskId) => {
    return quest.tasks?.find(t => t.id === taskId)?.is_completed || completedTaskIds.includes(taskId);
  };

  const completedTasks = quest.tasks?.filter(t => isTaskCompleted(t.id)).length || 0;
  const totalTasks = quest.tasks?.length || 1;
  const progress = (completedTasks / totalTasks) * 100;
  const isComplete = progress === 100;

  const handleCompleteTask = (task) => {
    setTaskToComplete(task);
    setVerificationError(null);
    setProofScreenshot(null);
    setProofUrl('');
    setShowConfirmModal(true);
  };

  // Handle screenshot upload
  const handleScreenshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setVerificationError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setVerificationError('Image must be less than 5MB');
      return;
    }

    setUploadingProof(true);
    setVerificationError(null);

    try {
      // Convert to base64 for storage/display
      const reader = new FileReader();
      reader.onload = (event) => {
        setProofScreenshot(event.target.result);
        setUploadingProof(false);
      };
      reader.onerror = () => {
        setVerificationError('Failed to read image');
        setUploadingProof(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setVerificationError('Failed to upload image');
      setUploadingProof(false);
    }
  };

  const confirmTaskCompletion = async () => {
    if (!taskToComplete) return;
    
    setClaiming(true);
    setVerificationError(null);
    
    try {
      // Try to verify with backend API
      await questsAPI.verifyTask(taskToComplete.id, proofUrl || null, proofScreenshot || null);
      
      // Store completion with proof
      const completionData = {
        taskId: taskToComplete.id,
        completedAt: new Date().toISOString(),
        proofUrl: proofUrl || null,
        hasScreenshot: !!proofScreenshot,
      };
      
      const newCompleted = [...completedTaskIds, taskToComplete.id];
      setCompletedTaskIds(newCompleted);
      localStorage.setItem(`quest_${quest.id}_completed`, JSON.stringify(newCompleted));
      
      // Store proof data separately
      const proofKey = `quest_${quest.id}_task_${taskToComplete.id}_proof`;
      localStorage.setItem(proofKey, JSON.stringify(completionData));
      
      setShowConfirmModal(false);
      setTaskToComplete(null);
      setProofScreenshot(null);
      setProofUrl('');
    } catch (err) {
      // On API error, still allow local completion (manual verification)
      const newCompleted = [...completedTaskIds, taskToComplete.id];
      setCompletedTaskIds(newCompleted);
      localStorage.setItem(`quest_${quest.id}_completed`, JSON.stringify(newCompleted));
      
      setShowConfirmModal(false);
      setTaskToComplete(null);
      setProofScreenshot(null);
      setProofUrl('');
    }
    
    setClaiming(false);
  };

  // Get tracking URL for visit-type tasks
  const getTaskUrl = (task) => {
    if (!task.action_url) return null;
    
    // For visit-type quests, generate tracking link
    if (quest.quest_type === 'visit') {
      return generateTrackingLink(quest.id, task.id, task.action_url);
    }
    return task.action_url;
  };

  // Determine verification type based on quest type
  const getVerificationType = () => {
    switch (quest.quest_type) {
      case 'share':
        return { label: 'Share Link', placeholder: 'Paste the URL of your post/share', requiresProof: true };
      case 'follow':
        return { label: 'Profile Screenshot', placeholder: 'Upload a screenshot showing you follow', requiresProof: true };
      case 'visit':
        return { label: 'Visit Proof', placeholder: 'Upload screenshot of the page you visited', requiresProof: false };
      case 'review':
        return { label: 'Review Link', placeholder: 'Paste the URL of your review', requiresProof: true };
      case 'referral':
        return { label: 'Referral Code', placeholder: 'Your unique referral code is below', requiresProof: false };
      default:
        return { label: 'Proof', placeholder: 'Provide proof of completion', requiresProof: false };
    }
  };

  const verificationType = getVerificationType();
  const typeConfig = QUEST_TYPE_CONFIG[quest.quest_type] || {};

  return (
    <>
      <Card className={`bg-[#1A1A1A] border-white/5 hover:border-purple-500/30 transition-all ${isComplete ? 'border-green-500/30 bg-green-500/5' : ''}`} data-testid={`quest-card-${quest.id}`}>
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`w-12 h-12 rounded-xl bg-${typeConfig.color || 'purple'}-500/20 flex items-center justify-center flex-shrink-0`}>
              <QuestTypeIcon type={quest.quest_type} className={`w-6 h-6 text-${typeConfig.color || 'purple'}-400`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <QuestTypeBadge type={quest.quest_type} />
                {quest.featured && <Badge className="bg-purple-500 text-white text-[10px] border-0">FEATURED</Badge>}
                {quest.is_onboarding && <Badge className="bg-orange-500 text-white text-[10px] border-0">GET STARTED</Badge>}
              </div>
              <h3 className="text-white font-semibold">{quest.title}</h3>
              <p className="text-white/50 text-sm line-clamp-1">{quest.description}</p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 font-bold">+{quest.xp_reward} XP</span>
              </div>
              <div className="flex items-center gap-1.5 text-white/30 text-sm">
                <Clock className="w-3 h-3" />
                {quest.time_estimate}
              </div>
            </div>
            <span className="text-white/30 text-xs">{quest.completions_count?.toLocaleString()} completed</span>
          </div>

          {/* Progress */}
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-white/50">Progress</span>
              <span className={isComplete ? 'text-green-400' : 'text-white'}>{completedTasks}/{totalTasks}</span>
            </div>
            <Progress value={progress} className={`h-2 ${isComplete ? '[&>div]:bg-green-500' : ''}`} />
          </div>

          {/* Expand Button */}
          <Button 
            variant="ghost" 
            className="w-full text-white/50 hover:text-white hover:bg-white/5 h-8" 
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={`quest-expand-btn-${quest.id}`}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
            {isExpanded ? "Hide Steps" : "View Steps"}
          </Button>

          {/* Expanded Tasks */}
          <AnimatePresence>
            {isExpanded && quest.tasks && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }} 
                animate={{ height: "auto", opacity: 1 }} 
                exit={{ height: 0, opacity: 0 }} 
                className="mt-3 space-y-2 overflow-hidden"
              >
                {quest.tasks.map((task, idx) => {
                  const completed = isTaskCompleted(task.id);
                  
                  return (
                    <div 
                      key={task.id} 
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        completed 
                          ? "bg-green-500/10 border border-green-500/20" 
                          : "bg-white/5 border border-white/5"
                      }`}
                      data-testid={`task-${task.id}`}
                    >
                      {/* Step Number / Checkmark */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        completed ? "bg-green-500" : "bg-white/10"
                      }`}>
                        {completed 
                          ? <Check className="w-4 h-4 text-white" /> 
                          : <span className="text-sm font-medium text-white/50">{idx + 1}</span>
                        }
                      </div>
                      
                      {/* Task Info */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${completed ? "text-green-400" : "text-white"}`}>
                          {task.title}
                        </p>
                        <p className="text-xs text-white/40">{task.description}</p>
                      </div>
                      
                      {/* XP Reward */}
                      <span className="text-xs text-orange-400 font-medium">+{task.xp_reward} XP</span>
                      
                      {/* Actions */}
                      {!completed && (
                        <div className="flex items-center gap-2">
                          {task.action_url && (
                            <a href={getTaskUrl(task)} target={task.action_url.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer">
                              <Button 
                                size="sm" 
                                className="h-8 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 text-xs"
                                data-testid={`task-go-btn-${task.id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                {task.action_label || 'Go'}
                              </Button>
                            </a>
                          )}
                          <Button 
                            size="sm" 
                            className="h-8 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs"
                            onClick={() => handleCompleteTask(task)}
                            data-testid={`task-verify-btn-${task.id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Done
                          </Button>
                        </div>
                      )}
                      
                      {completed && (
                        <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">
                          <Check className="w-3 h-3 mr-1" />Verified
                        </Badge>
                      )}
                    </div>
                  );
                })}
                
                {/* All Done Message */}
                {isComplete && (
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-center">
                    <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">Quest Complete!</p>
                    <p className="text-white/50 text-sm">You earned {quest.xp_reward} XP</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Verification Modal with Screenshot Upload */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Verify Completion
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Upload proof to verify your task completion
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {taskToComplete && (
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <p className="text-white font-medium">{taskToComplete.title}</p>
                <p className="text-white/50 text-sm mt-1">{taskToComplete.description}</p>
                <p className="text-orange-400 text-sm mt-2 font-medium">+{taskToComplete.xp_reward} XP</p>
              </div>
            )}

            {/* Verification Type Info */}
            <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <p className="text-purple-400 text-sm font-medium mb-1">{verificationType.label}</p>
              <p className="text-white/50 text-xs">{verificationType.placeholder}</p>
            </div>

            {/* Proof URL Input (for share, review quests) */}
            {(quest.quest_type === 'share' || quest.quest_type === 'review') && (
              <div>
                <label className="text-white/70 text-sm block mb-2">Proof URL (optional)</label>
                <input
                  type="url"
                  value={proofUrl}
                  onChange={(e) => setProofUrl(e.target.value)}
                  placeholder="https://twitter.com/yourpost..."
                  className="w-full px-3 py-2 bg-[#0D0D0D] border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm focus:border-purple-500/50 focus:outline-none"
                />
              </div>
            )}

            {/* Referral Code Display (for referral quests) */}
            {quest.quest_type === 'referral' && (
              <div className="p-4 bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-orange-500/20 rounded-xl">
                <p className="text-white/70 text-xs mb-2">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-black/30 rounded-lg text-orange-400 font-mono text-lg">
                    BURNZ-XXXXXX
                  </code>
                  <Button size="sm" variant="outline" className="border-white/10 text-white/70">
                    Copy
                  </Button>
                </div>
                <p className="text-white/40 text-xs mt-2">Share this code to earn rewards when friends join!</p>
              </div>
            )}

            {/* Screenshot Upload */}
            <div>
              <label className="text-white/70 text-sm block mb-2">
                Upload Screenshot {verificationType.requiresProof ? '(recommended)' : '(optional)'}
              </label>
              
              {!proofScreenshot ? (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-purple-500/50 hover:bg-purple-500/5 transition-all">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotUpload}
                    className="hidden"
                    disabled={uploadingProof}
                  />
                  {uploadingProof ? (
                    <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mb-2" />
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2">
                        <Camera className="w-5 h-5 text-white/50" />
                      </div>
                      <p className="text-white/50 text-sm">Click to upload screenshot</p>
                      <p className="text-white/30 text-xs mt-1">PNG, JPG up to 5MB</p>
                    </>
                  )}
                </label>
              ) : (
                <div className="relative">
                  <img 
                    src={proofScreenshot} 
                    alt="Proof screenshot" 
                    className="w-full h-32 object-cover rounded-xl border border-white/10"
                  />
                  <button
                    onClick={() => setProofScreenshot(null)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500/80 flex items-center justify-center hover:bg-red-500"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-green-500/80 rounded text-xs text-white font-medium">
                    ✓ Screenshot uploaded
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {verificationError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {verificationError}
                </p>
              </div>
            )}
            
            {/* Warning */}
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-400 text-xs">
                ⚠️ False claims may result in account penalties. Only submit if you&apos;ve genuinely completed the task.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-white/10 text-white/50"
                onClick={() => {
                  setShowConfirmModal(false);
                  setProofScreenshot(null);
                  setProofUrl('');
                }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={confirmTaskCompletion}
                disabled={claiming}
                data-testid="confirm-task-completion-btn"
              >
                {claiming ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {proofScreenshot ? 'Submit with Proof' : 'Confirm Done'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Quests Page
export const QuestsPage = () => {
  const { user } = useApp();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch quests from API
  useEffect(() => {
    const fetchQuests = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await questsAPI.getAll();
        if (data && data.quests && data.quests.length > 0) {
          setQuests(data.quests);
        } else if (Array.isArray(data) && data.length > 0) {
          setQuests(data);
        } else {
          // Fallback to mock data if API returns empty
          setQuests(DEMO.quests || []);
        }
      } catch (err) {
        // Fallback to mock data on error
        setQuests(DEMO.quests || []);
        setError('Using offline data');
      }
      setLoading(false);
    };

    fetchQuests();
  }, []);

  // Get onboarding quest
  const onboardingQuest = quests.find(q => q.is_onboarding);
  const onboardingCompleted = localStorage.getItem(`quest_${onboardingQuest?.id}_completed`);
  const onboardingTasksCompleted = onboardingCompleted ? JSON.parse(onboardingCompleted).length : (onboardingQuest?.tasks?.filter(t => t.is_completed).length || 0);
  const onboardingProgress = onboardingQuest ? (onboardingTasksCompleted / (onboardingQuest.tasks?.length || 1)) * 100 : 0;

  // Filter quests
  const filters = [
    { id: "all", label: "All Quests", icon: Target },
    { id: "share", label: "Share", icon: Share2, color: "orange" },
    { id: "visit", label: "Visit", icon: Globe, color: "blue" },
    { id: "follow", label: "Follow", icon: Users, color: "purple" },
    { id: "review", label: "Review", icon: Star, color: "yellow" },
    { id: "referral", label: "Referral", icon: Link2, color: "green" },
  ];

  const filteredQuests = quests.filter(q => {
    if (q.is_onboarding) return false; // Show onboarding separately
    if (activeFilter !== "all" && q.quest_type !== activeFilter) return false;
    if (searchQuery && !q.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Stats
  const totalXPAvailable = quests.reduce((sum, q) => sum + (q.xp_reward || 0), 0);
  const activeQuests = quests.filter(q => q.status === 'active' && !q.is_onboarding).length;

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
          <p className="text-white/50">Loading quests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="p-2.5 lg:p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <span className="text-yellow-400 text-xs lg:text-sm">{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-white flex items-center gap-2 lg:gap-3">
            <Target className="w-5 h-5 lg:w-7 lg:h-7 text-orange-400" />
            Quests
          </h1>
          <p className="text-white/50 text-xs lg:text-sm mt-0.5 lg:mt-1">
            Complete marketing campaigns and earn XP rewards
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-[#1A1A1A] rounded-xl border border-white/5">
          <Sparkles className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-orange-400" />
          <span className="text-white font-bold text-sm lg:text-base">{user?.xp || 2847} XP</span>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-2 lg:gap-4">
        {[
          { label: "Active Quests", value: activeQuests, icon: Target, color: "purple" },
          { label: "XP Available", value: `${(totalXPAvailable / 1000).toFixed(1)}K`, icon: Sparkles, color: "orange" },
          { label: "Your Completions", value: user?.total_quests_completed || 47, icon: CheckCircle, color: "green" },
          { label: "Daily Streak", value: `${user?.streak_days || 7} days`, icon: Zap, color: "yellow" },
        ].map((stat, i) => (
          <Card key={i} className="bg-[#1A1A1A] border-white/5">
            <CardContent className="p-4">
              <stat.icon className={`w-5 h-5 text-${stat.color}-400 mb-2`} />
              <p className="text-xl font-bold text-white">{stat.value}</p>
              <p className="text-white/50 text-xs">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Onboarding Quest Banner (if not complete) */}
      {onboardingQuest && onboardingProgress < 100 && (
        <Card className="bg-gradient-to-r from-orange-500/10 via-purple-500/10 to-cyan-500/10 border-orange-500/20">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-purple-500 flex items-center justify-center">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-orange-500 text-white border-0 text-xs">GET STARTED</Badge>
                  <span className="text-white/50 text-xs">{onboardingTasksCompleted}/{onboardingQuest.tasks.length} steps</span>
                </div>
                <h3 className="text-white font-bold">{onboardingQuest.title}</h3>
                <p className="text-white/50 text-sm">{onboardingQuest.description}</p>
                <Progress value={onboardingProgress} className="h-2 mt-3" />
              </div>
              <div className="text-right">
                <p className="text-orange-400 font-bold">+{onboardingQuest.xp_reward} XP</p>
                <Button className="mt-2 bg-orange-500 hover:bg-orange-600 text-white" size="sm">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quest Type Info */}
      <Card className="bg-[#1A1A1A] border-white/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-8">
            <div>
              <h3 className="text-white font-bold text-sm mb-1">Quest Types</h3>
              <p className="text-white/40 text-xs">Real marketing actions with verified outcomes</p>
            </div>
            <div className="flex-1 flex items-center gap-4">
              {Object.entries(QUEST_TYPE_CONFIG).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2 text-white/50 text-xs">
                  <span className="text-base">{config.icon}</span>
                  <span>{config.label}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters & Search */}
      <div className="flex items-center gap-4">
        <div className="flex bg-[#1A1A1A] rounded-xl p-1 border border-white/5">
          {filters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilter === filter.id 
                  ? "bg-purple-500 text-white" 
                  : "text-white/50 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`filter-${filter.id}`}
            >
              <filter.icon className="w-4 h-4" />
              {filter.label}
            </button>
          ))}
        </div>
        
        <div className="flex-1" />
        
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input 
            placeholder="Search quests..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#1A1A1A] border-white/10 pl-10 text-white placeholder:text-white/30"
            data-testid="search-quests-input"
          />
        </div>
      </div>

      {/* Quest Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredQuests.map(quest => (
          <QuestCard key={quest.id} quest={quest} />
        ))}
      </div>

      {filteredQuests.length === 0 && (
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">No quests found</h3>
            <p className="text-white/50">Try adjusting your filters or search query</p>
          </CardContent>
        </Card>
      )}

      {/* How Quests Work */}
      <Card className="bg-[#1A1A1A] border-white/5">
        <CardHeader>
          <CardTitle className="text-white text-lg">How Quests Work</CardTitle>
          <CardDescription className="text-white/50">
            Simple steps to earn XP rewards
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-6">
            {[
              { step: 1, title: "What to do", desc: "Each quest has clear actions", icon: Target, color: "purple" },
              { step: 2, title: "Why it matters", desc: "Real marketing value for brands", icon: Zap, color: "blue" },
              { step: 3, title: "How to verify", desc: "Simple self-verification", icon: CheckCircle, color: "orange" },
              { step: 4, title: "What you earn", desc: "XP for every action", icon: Sparkles, color: "green" },
            ].map(item => (
              <div key={item.step} className="text-center">
                <div className={`w-12 h-12 rounded-2xl bg-${item.color}-500/20 flex items-center justify-center mx-auto mb-3`}>
                  <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <div className="text-purple-400 text-sm font-bold mb-1">Step {item.step}</div>
                <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                <p className="text-white/50 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuestsPage;
